"use client";

import { useEffect, useRef } from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { MODAL_VIEWPORT_FRAME_SM } from "@/components/ui/modal-surface";
import {
  trackUpgradeModalShown,
  trackUpgradeModalDismissed,
} from "@/lib/analytics";
import {
  RatedCreditsFlow,
  type RatedEntry,
  type RatedInitialStatus,
  type RatedChoosePlanCopy,
  type RatedPersonalization,
  type Screen,
} from "./rated-credits-flow";
import type { PaidPlanId } from "@/lib/plans";
import type { UpgradeCopyVariant } from "@/lib/billing/upgrade-copy-experiment";

/**
 * Modal host for the rated credits & plans experience. Opened from the
 * out-of-credits paywall when NEXT_PUBLIC_RATED_BILLING_ENABLED is on, so the
 * user gets the new credits/upgrade flow inline (free → choose-a-plan, paid →
 * upgrade/auto-topup/buy) instead of the legacy UpgradeModal / CreditExhausted
 * / Topup modals. RatedCreditsFlow brings its own card (Shell) + close button
 * and fetches its own status on mount; this only provides the overlay,
 * backdrop, and centering. Mounting the flow only while `open` (the `{open &&}`
 * guard) makes each open refetch the balance/plan — so a top-up that lands and
 * returns shows the fresh number.
 */
export function RatedCreditsModal({
  open,
  onClose,
  entry,
  startScreen,
  returnTo,
  initialStatus,
  source,
  intent,
  initialPlan,
  initialCredits,
  insufficientCredits,
  personalization,
  choosePlanCopy,
}: {
  open: boolean;
  onClose: () => void;
  entry?: RatedEntry;
  /** Jump straight to a specific screen (e.g. "upgrade" from a Manage menu),
   *  overriding the entry-resolved default. */
  startScreen?: Screen;
  /** Page Stripe Checkout returns to (must run the reconcile-on-return effect).
   *  Pass the host page so a purchase lands back where it started. */
  returnTo?: string;
  /** Known plan/credits to seed the flow with, so it opens instantly with no
   *  loading flash. */
  initialStatus?: RatedInitialStatus;
  /** Entry-point source forwarded onto checkout (e.g. "stripe_button"). */
  source?: string;
  /** Gated intent forwarded onto checkout (e.g. "connect_stripe"). */
  intent?: string;
  /** Plan the Choose-a-plan screen recommends (defaults to "pro"). Every plan
   *  is shown either way — this only sets the emphasis. */
  initialPlan?: PaidPlanId;
  /** Credit amount the picker opens on — used to restore the selection a user
   *  made before leaving for Stripe and coming back without paying. */
  initialCredits?: number;
  /** Positive-but-below-cost balance (PANDA-1367): name the shortfall on the
   *  choose-plan screen so the paywall never reads as "you're out" over a
   *  still-positive counter. Null for a truly-empty pool. */
  insufficientCredits?: { available: number; required?: number } | null;
  /** Context for the mid-session personalised out-of-credits headline (#2). */
  personalization?: RatedPersonalization | null;
  /** Deterministic headline/subheading for a non-credit Choose-plan gate. */
  choosePlanCopy?: RatedChoosePlanCopy;
}) {
  // Fire the upgrade-modal impression once per open (reset on close), mirroring
  // the shownRef pattern used by the other credit/paywall modals.
  //
  // It now waits for the copy A/B arm, which the flow reports once its status
  // read settles (RatedCreditsFlow.onCopyVariantResolved — it always fires,
  // falling back to "control" on failure). Firing before then would stamp every
  // impression "control" while variant-arm users saw the rewrite, which is
  // exactly the mislabelling that would invalidate the primary metric. The
  // dismissal event reads the same state, so a modal closed before the arm
  // lands reports "control" — matching the copy that was actually on screen.
  const shownRef = useRef(false);
  const armRef = useRef<UpgradeCopyVariant | null>(null);

  // The impression is fired from the flow's callback rather than an open-effect
  // precisely because it must carry the arm: the arm isn't known until the
  // status read settles, and there is nothing to report before then. Both
  // trackers live in refs so closing resets them without a state update inside
  // an effect (which would cascade renders — react-hooks/set-state-in-effect).
  const handleCopyVariantResolved = (v: UpgradeCopyVariant) => {
    armRef.current = v;
    if (shownRef.current) return;
    shownRef.current = true;
    trackUpgradeModalShown({
      entry_point: "rated_credits_modal",
      surface: "workspace",
      source,
      copy_variant: v,
    });
  };

  useEffect(() => {
    if (!open) {
      shownRef.current = false;
      armRef.current = null;
    }
  }, [open]);

  // Both dismissal paths (backdrop/esc and the flow's own close/back control)
  // funnel through this single wrapper so the event fires exactly once.
  const handleClose = () => {
    trackUpgradeModalDismissed({
      entry_point: "rated_credits_modal",
      surface: "workspace",
      source,
      copy_variant: armRef.current ?? "control",
    });
    onClose();
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogPrimitive.Portal>
        {/* z-[100]: above the in-project chat panel (z-60) so the paywall is
            never trapped behind it on mobile — matches the legacy
            mobile-upgrade-modal's full-screen layer. */}
        <DialogPrimitive.Backdrop
          className={cn(
            "fixed inset-0 z-[100] bg-black/50 supports-backdrop-filter:backdrop-blur-sm",
            "data-open:animate-in data-open:fade-in-0",
            "data-closed:animate-out data-closed:fade-out-0",
          )}
        />
        <DialogPrimitive.Popup
          className={cn(
            // Phones: full-screen sheet (the card inside fills it edge-to-edge).
            "fixed inset-0 z-[100] flex flex-col overflow-hidden",
            // The popup is the FRAME, never the scroller — the Shell inside owns
            // the one scroll region, which is what keeps the ✕ and the headline
            // pinned while the body scrolls. sm:rounded-3xl clips that scrollbar
            // to the card's rounded shape. (PANDA-1565)
            "sm:rounded-3xl",
            // ≥sm: the centered pop-up card, bounded to the DYNAMIC viewport so a
            // mobile browser's visible toolbars can't push it off screen.
            "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-[calc(100%-2rem)] sm:max-w-140 sm:-translate-x-1/2 sm:-translate-y-1/2",
            MODAL_VIEWPORT_FRAME_SM,
            // ≥lg: headroom for the choose-a-plan screen, which shows every plan
            // side by side. Narrower screens keep their own max-width inside
            // (Shell) and stay centred, so nothing else changes width.
            "lg:max-w-4xl",
            "data-open:animate-in data-open:fade-in-0 sm:data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 sm:data-closed:zoom-out-95",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Credits &amp; plans
          </DialogPrimitive.Title>
          {open && (
            <RatedCreditsFlow
              fullScreenOnMobile
              entry={entry}
              startScreen={startScreen}
              returnTo={returnTo}
              initialStatus={initialStatus}
              source={source}
              intent={intent}
              initialPlan={initialPlan}
              initialCredits={initialCredits}
              insufficientCredits={insufficientCredits}
              personalization={personalization}
              choosePlanCopy={choosePlanCopy}
              onCopyVariantResolved={handleCopyVariantResolved}
              onClose={handleClose}
            />
          )}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
